<template>
  <div class="rso-page">
    <div class="rso-page-header">
      <h1 class="rso-page-title">Отчет Бюджет проекта</h1>
    </div>

    <div class="rso-filters-card">
      <div class="rso-filters-card__body">
        <div class="rso-filters-toolbar">
          <div class="rso-filters-main">
            <CustomSelect
              :options="residentOptions"
              option-label-key="resident_name"
              option-value-key="resident_id"
              label="Жилой комплекс"
              :value="residentId"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!residentId"
              class="rso-filter-field"
              @change="onResidentChange"
            />
            <CustomSelect
              :options="projectTypeOptions"
              option-value-key="value"
              option-label-key="label"
              label="Тип проекта"
              :value="projectTypeFilter"
              fullWidth
              :useInput="false"
              class="rso-filter-field"
              @change="onProjectTypeChange"
            />
            <CustomSelect
              :options="blockFilterOptions"
              option-value-key="value"
              option-label-key="label"
              label="Блок"
              clearable
              :disable="!residentId"
              :loading="isBlockOptionsLoading"
              :value="blockFilter"
              fullWidth
              :useInput="false"
              class="rso-filter-field"
              @change="onBlockChange"
            />
            <CustomSelect
              :options="floorFilterOptions"
              option-value-key="value"
              option-label-key="label"
              label="Этаж"
              clearable
              :disable="!residentId"
              :loading="isFloorOptionsLoading"
              :value="floorFilter"
              fullWidth
              :useInput="false"
              class="rso-filter-field"
              @change="onFloorChange"
            />
            <CustomDate
              :value="agreementDateFrom"
              label="Дата договора с"
              placeholder="Выберите.."
              fullWidth
              :disabled="!residentId"
              class="rso-filter-field rso-filter-field--date"
              @change_date="onAgreementDateFromChange"
            />
          </div>

          <div class="rso-filters-actions">
            <q-btn
              class="rso-filter-action-btn"
              outline
              color="grey-7"
              no-caps
              dense
              icon="filter_alt_off"
              label="Сбросить"
              :disable="!residentId || !hasActiveFilters"
              @click="resetFilters"
            />
            <q-btn
              class="rso-filter-action-btn"
              :outline="!isAdvancedFiltersOpen"
              :unelevated="isAdvancedFiltersOpen"
              :color="advancedFiltersBtnColor"
              no-caps
              dense
              :icon-right="isAdvancedFiltersOpen ? 'expand_less' : 'expand_more'"
              label="Фильтры"
              :disable="!residentId"
              @click="toggleAdvancedFilters"
            >
              <q-badge
                v-if="hasActiveAdvancedFilters"
                color="orange"
                floating
                rounded
              />
            </q-btn>
            <q-btn
              class="rso-filter-action-btn"
              color="positive"
              unelevated
              no-caps
              dense
              icon="download"
              label="Excel"
              :disable="!residentId || isExporting"
              :loading="isExporting"
              @click="exportExcel"
            />
          </div>
        </div>

        <div
          v-show="isAdvancedFiltersOpen"
          class="rso-filters-advanced"
        >
          <CustomSelect
            :options="workGroupFilterOptions"
            option-value-key="value"
            option-label-key="label"
            label="Группа работ"
            clearable
            :disable="!residentId"
            :loading="isScopeOptionsLoading"
            :value="workGroupFilter"
            fullWidth
            :useInput="false"
            class="rso-filter-field"
            @change="onWorkGroupFilterChange"
          />
          <CustomSelect
            :options="workSetFilterOptions"
            option-value-key="value"
            option-label-key="label"
            label="Работа"
            clearable
            :disable="!residentId"
            :loading="isScopeOptionsLoading"
            :value="workSetFilter"
            fullWidth
            :useInput="false"
            class="rso-filter-field"
            @change="onWorkSetFilterChange"
          />
          <CustomSelect
            :options="materialFilterOptions"
            option-value-key="value"
            option-label-key="label"
            label="Материал"
            clearable
            :disable="!residentId"
            :loading="isScopeOptionsLoading"
            :value="materialFilter"
            fullWidth
            :useInput="false"
            class="rso-filter-field"
            @change="onMaterialFilterChange"
          />
        </div>
      </div>
    </div>

    <q-linear-progress v-if="isFetching" indeterminate class="rso-progress" />

    <div v-if="!residentId" class="rso-hint">
      Выберите жилой комплекс, чтобы загрузить актуальную смету по всем проектам.
    </div>

    <NotFoundText v-else-if="!isFetching && !treeRoots.length" />

    <div v-else-if="treeRoots.length" class="rso-tree-card q-mt-md">
      <div class="rso-tree-toolbar">
        <div class="rso-tree-toolbar__actions">
          <q-btn
            class="rso-tree-action-btn"
            flat
            dense
            no-caps
            size="sm"
            :icon-right="isTreeFullyExpanded ? 'unfold_less' : 'unfold_more'"
            :label="isTreeFullyExpanded ? 'Свернуть всё' : 'Развернуть всё'"
            :disable="isFetching"
            @click="toggleExpandAll"
          />
        </div>
        <div
          v-if="grandTotalNoNds != null || grandTotalWithNds != null"
          class="rso-tree-toolbar__total"
        >
          <span class="rso-tree-toolbar__total-label">Общая стоимость:</span>
          <span v-if="grandTotalWithNds != null" class="rso-tree-toolbar__total-item">
            <span class="rso-tree-toolbar__total-suffix">с НДС</span>
            <Amount
              :value="grandTotalWithNds"
              :round="2"
              class="rso-tree-toolbar__total-value"
            />
          </span>
          <span
            v-if="grandTotalNoNds != null && grandTotalWithNds != null"
            class="rso-tree-toolbar__total-divider"
            aria-hidden="true"
          />
          <span v-if="grandTotalNoNds != null" class="rso-tree-toolbar__total-item">
            <span class="rso-tree-toolbar__total-suffix">без НДС</span>
            <Amount
              :value="grandTotalNoNds"
              :round="2"
              class="rso-tree-toolbar__total-value"
            />
          </span>
        </div>
      </div>
      <div class="rso-tree-scroll">
        <div
          class="rso-tree-grid"
          :style="{ minWidth: `${RESIDENT_SMETA_GRID_MIN_WIDTH_PX}px` }"
        >
        <div class="rso-grid-header-sticky">
          <table
            class="rso-mini-table rso-smeta-grid-table"
            lang="ru"
          >
            <colgroup>
              <col
                v-for="c in RESIDENT_SMETA_GRID_COLS"
                :key="c.key"
                :style="{ width: c.width }"
              />
            </colgroup>
            <ResidentSmetaGridHeader />
          </table>
        </div>
        <ResidentSmetaTreeNode
          v-for="root in treeRoots"
          :key="root.id"
          :node="root"
          :depth="0"
          :expanded-ids="expandedIdsSet"
          @toggle="onToggleExpand"
        />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useQuery } from "@tanstack/vue-query";
import CustomSelect from "@/components/form/CustomSelect";
import CustomDate from "@/components/form/CustomDate";
import NotFoundText from "@/components/common/NotFoundText";
import Amount from "@/components/common/Amount.vue";
import ResidentSmetaGridHeader from "./components/ResidentSmetaGridHeader.vue";
import ResidentSmetaTreeNode from "./components/ResidentSmetaTreeNode.vue";
import { RESIDENT_SMETA_GRID_COLS, RESIDENT_SMETA_GRID_MIN_WIDTH_PX } from "./residentSmetaGridColumns.js";
import {
  buildResidentSmetaFilterOptionsFromRaw,
  buildResidentSmetaTree,
  collectResidentSmetaExpandableIds,
  getResidentSmetaExpandedIdsForFilters,
  isResidentSmetaFilterValueActive,
} from "@/_helpers/residentSmetaTree.js";
import { buildSmetaNewTableRows } from "@/_helpers/smetaNewTableRows.js";
import {
  exportResidentSmetaNewReport,
  fetchResidentSmetaNewRaw,
  getResidentSmetaBlockOptions,
  getResidentSmetaFloorOptions,
} from "@/views/ProjectsEntry/services/index.js";
import { getResidentList, getProjectTypes } from "@/views/ResidentialSettings/services/index.js";

const residentId = ref(null);
const projectTypeFilter = ref(null);
const blockFilter = ref(null);
const floorFilter = ref(null);
const workGroupFilter = ref(null);
const workSetFilter = ref(null);
const materialFilter = ref(null);
const agreementDateFrom = ref(null);

const residents = ref([]);
const projectTypes = ref([]);
const blockOptions = ref([]);
const floorOptions = ref([]);
const workGroupOptions = ref([]);
const workSetOptions = ref([]);
const materialOptions = ref([]);
const isBlockOptionsLoading = ref(false);
const isFloorOptionsLoading = ref(false);
const isScopeOptionsLoading = ref(false);
const isExporting = ref(false);
const isAdvancedFiltersOpen = ref(false);

const expandedIdsSet = ref(new Set());

const residentOptions = computed(() => residents.value || []);

const projectTypeOptions = computed(() => {
  const base = [{ label: "Все типы", value: null }];
  const list = projectTypes.value || [];
  for (const pt of list) {
    base.push({
      label: pt.project_type_name || String(pt.project_type_id),
      value: pt.project_type_id,
    });
  }
  return base;
});

const blockFilterOptions = computed(() => {
  const base = [{ label: "Все блоки", value: null }];
  return base.concat(blockOptions.value || []);
});

const floorFilterOptions = computed(() => {
  const base = [{ label: "Все этажи", value: null }];
  return base.concat(floorOptions.value || []);
});

const workGroupFilterOptions = computed(() => {
  const base = [{ label: "Все группы", value: null }];
  return base.concat(workGroupOptions.value || []);
});

const workSetFilterOptions = computed(() => {
  const base = [{ label: "Все работы", value: null }];
  return base.concat(workSetOptions.value || []);
});

const materialFilterOptions = computed(() => {
  const base = [{ label: "Все материалы", value: null }];
  return base.concat(materialOptions.value || []);
});

async function loadResidents() {
  const list = await getResidentList();
  residents.value = Array.isArray(list) ? list : [];
}

async function loadProjectTypes(rid) {
  if (!rid) {
    projectTypes.value = [];
    return;
  }
  const list = await getProjectTypes(rid);
  projectTypes.value = Array.isArray(list) ? list : [];
}

async function loadBlockOptions() {
  const rid = residentId.value;
  if (!rid) {
    blockOptions.value = [];
    return;
  }
  isBlockOptionsLoading.value = true;
  const list = await getResidentSmetaBlockOptions(rid, projectTypeFilter.value);
  isBlockOptionsLoading.value = false;
  blockOptions.value = list;
  await loadFloorOptions();
}

async function loadFloorOptions() {
  const rid = residentId.value;
  if (!rid) {
    floorOptions.value = [];
    return;
  }
  isFloorOptionsLoading.value = true;
  const entranceIds = blockFilter.value
    ? null
    : (blockOptions.value || []).map((item) => item.value).filter(Boolean);
  const list = await getResidentSmetaFloorOptions({
    resident_id: rid,
    project_type_id: projectTypeFilter.value,
    project_entrance_id: blockFilter.value,
    project_entrance_ids: entranceIds,
  });
  isFloorOptionsLoading.value = false;
  floorOptions.value = list;
  await loadScopeFilterOptions();
}

const hasActiveAdvancedFilters = computed(
  () =>
    isResidentSmetaFilterValueActive(workGroupFilter.value) ||
    isResidentSmetaFilterValueActive(workSetFilter.value) ||
    isResidentSmetaFilterValueActive(materialFilter.value)
);

const hasActiveFilters = computed(
  () =>
    isResidentSmetaFilterValueActive(projectTypeFilter.value) ||
    isResidentSmetaFilterValueActive(blockFilter.value) ||
    isResidentSmetaFilterValueActive(floorFilter.value) ||
    !!agreementDateFrom.value ||
    hasActiveAdvancedFilters.value
);

const advancedFiltersBtnColor = computed(() => {
  if (isAdvancedFiltersOpen.value) return "primary";
  if (hasActiveAdvancedFilters.value) return "primary";
  return "grey-7";
});

function toggleAdvancedFilters() {
  isAdvancedFiltersOpen.value = !isAdvancedFiltersOpen.value;
}

watch(hasActiveAdvancedFilters, (active) => {
  if (active) isAdvancedFiltersOpen.value = true;
});

function resetWorkFilters() {
  workGroupFilter.value = null;
  workSetFilter.value = null;
  materialFilter.value = null;
  workGroupOptions.value = [];
  workSetOptions.value = [];
  materialOptions.value = [];
}

/** Справочники фильтров — тот же resident_agreement_smeta_report без фильтра по работе/материалу. */
async function loadScopeFilterOptions() {
  const rid = residentId.value;
  if (!rid) {
    workGroupOptions.value = [];
    workSetOptions.value = [];
    materialOptions.value = [];
    return;
  }

  isScopeOptionsLoading.value = true;
  const raw = await fetchResidentSmetaNewRaw(
    rid,
    projectTypeFilter.value,
    blockFilter.value,
    floorFilter.value,
    null,
    null,
    null,
    agreementDateFrom.value
  );
  isScopeOptionsLoading.value = false;

  const opts = buildResidentSmetaFilterOptionsFromRaw(raw);
  workGroupOptions.value = opts.workGroups;
  workSetOptions.value = opts.workSets;
  materialOptions.value = opts.materials;
}

function resetTreeExpand() {
  expandedIdsSet.value = new Set();
}

watch(
  () => residentId.value,
  (rid) => {
    loadProjectTypes(rid);
    blockFilter.value = null;
    floorFilter.value = null;
    blockOptions.value = [];
    floorOptions.value = [];
    resetWorkFilters();
    resetTreeExpand();
    if (rid) {
      loadBlockOptions();
    }
  },
  { immediate: true }
);

watch(
  () => projectTypeFilter.value,
  () => {
    blockFilter.value = null;
    floorFilter.value = null;
    resetWorkFilters();
    if (residentId.value) {
      loadBlockOptions();
    }
  }
);

watch(
  () => blockFilter.value,
  () => {
    floorFilter.value = null;
    resetWorkFilters();
    if (residentId.value) {
      loadFloorOptions();
    }
  }
);

const selectedFloorMeta = computed(() =>
  (floorOptions.value || []).find((item) => item.value === floorFilter.value)
);

function onResidentChange(v) {
  residentId.value = v;
  projectTypeFilter.value = null;
}

function onProjectTypeChange(v) {
  projectTypeFilter.value = v;
  applyFilterExpand();
}

function onBlockChange(v) {
  blockFilter.value = v;
  applyFilterExpand();
}

function onFloorChange(v) {
  floorFilter.value = v;
  resetWorkFilters();
  applyFilterExpand();
  if (residentId.value) {
    loadScopeFilterOptions();
  }
}

function onAgreementDateFromChange(v) {
  agreementDateFrom.value = v || null;
  resetWorkFilters();
  applyFilterExpand();
  if (residentId.value) {
    loadScopeFilterOptions();
  }
}

function applyFilterExpand() {
  if (!residentId.value) {
    resetTreeExpand();
    return;
  }
  if (!hasActiveFilters.value) {
    resetTreeExpand();
    return;
  }
  expandedIdsSet.value = getResidentSmetaExpandedIdsForFilters(treeRoots.value, {
    project_type_id: projectTypeFilter.value,
    project_entrance_id: blockFilter.value,
    floor_map_id: floorFilter.value,
    level_num: selectedFloorMeta.value?.level_num ?? null,
    work_set_check_group_id: workGroupFilter.value,
    work_set_id: workSetFilter.value,
    material_id: materialFilter.value,
  });
}

function onWorkGroupFilterChange(v) {
  workGroupFilter.value = v;
  applyFilterExpand();
}

function onWorkSetFilterChange(v) {
  workSetFilter.value = v;
  applyFilterExpand();
}

function onMaterialFilterChange(v) {
  materialFilter.value = v;
  applyFilterExpand();
}

function resetFilters() {
  projectTypeFilter.value = null;
  blockFilter.value = null;
  floorFilter.value = null;
  agreementDateFrom.value = null;
  resetWorkFilters();
  resetTreeExpand();
  if (residentId.value) {
    loadBlockOptions();
  }
}

const queryKey = computed(() => [
  "residentSmetaOverview",
  residentId.value,
  projectTypeFilter.value,
  blockFilter.value,
  floorFilter.value,
  workGroupFilter.value,
  workSetFilter.value,
  materialFilter.value,
  agreementDateFrom.value,
]);

const { data: queryData, isFetching } = useQuery({
  queryKey,
  queryFn: async () => {
    const rid = residentId.value;
    if (!rid) return { tree: [] };
    const raw = await fetchResidentSmetaNewRaw(
      rid,
      projectTypeFilter.value,
      blockFilter.value,
      floorFilter.value,
      workGroupFilter.value,
      workSetFilter.value,
      materialFilter.value,
      agreementDateFrom.value
    );
    const table = buildSmetaNewTableRows(raw, { withProjectKey: true });
    const resName =
      residents.value.find((r) => r.resident_id === rid)?.resident_name ||
      raw[0]?.resident_name ||
      "";
    const tree = buildResidentSmetaTree(raw, resName, table);
    return { tree };
  },
  enabled: computed(() => !!residentId.value),
});

const treeRoots = computed(() => queryData.value?.tree || []);

watch(treeRoots, () => {
  if (!residentId.value) return;
  if (!hasActiveFilters.value) {
    resetTreeExpand();
    return;
  }
  expandedIdsSet.value = getResidentSmetaExpandedIdsForFilters(treeRoots.value, {
    project_type_id: projectTypeFilter.value,
    project_entrance_id: blockFilter.value,
    floor_map_id: floorFilter.value,
    level_num: selectedFloorMeta.value?.level_num ?? null,
    work_set_check_group_id: workGroupFilter.value,
    work_set_id: workSetFilter.value,
    material_id: materialFilter.value,
  });
});

const allExpandableIds = computed(() =>
  collectResidentSmetaExpandableIds(treeRoots.value)
);

const isTreeFullyExpanded = computed(() => {
  const ids = allExpandableIds.value;
  if (!ids.length) return false;
  const expanded = expandedIdsSet.value;
  return ids.every((id) => expanded.has(id));
});

function parseCostValue(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

const grandTotalNoNds = computed(() => {
  const root = treeRoots.value?.[0];
  return parseCostValue(root?.total_cost);
});

const grandTotalWithNds = computed(() => {
  const root = treeRoots.value?.[0];
  return parseCostValue(root?.total_cost_with_nds);
});

function onToggleExpand(id) {
  const next = new Set(expandedIdsSet.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  expandedIdsSet.value = next;
}

function expandAll() {
  const ids = collectResidentSmetaExpandableIds(treeRoots.value);
  expandedIdsSet.value = new Set(ids);
}

function collapseAll() {
  expandedIdsSet.value = new Set();
}

function toggleExpandAll() {
  if (isTreeFullyExpanded.value) collapseAll();
  else expandAll();
}

async function exportExcel() {
  const rid = residentId.value;
  if (!rid || isExporting.value) return;

  isExporting.value = true;
  try {
    await exportResidentSmetaNewReport({
      resident_id: rid,
      project_type_id: projectTypeFilter.value,
      project_entrance_id: blockFilter.value,
      floor_map_id: floorFilter.value,
      work_set_check_group_id: workGroupFilter.value,
      work_set_id: workSetFilter.value,
      material_id: materialFilter.value,
      agreement_date_from: agreementDateFrom.value,
    });
  } finally {
    isExporting.value = false;
  }
}

loadResidents();
</script>

<style scoped lang="scss">
@use "@/assets/styles/quasar.variables.scss" as *;

.rso-page {
  padding: 8px 12px 48px;
  max-width: 100%;
  min-height: calc(100vh - 56px);
  min-height: calc(100dvh - 56px);
  box-sizing: border-box;
}

.rso-page-header {
  margin-bottom: 8px;
}

.rso-page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  color: #0f172a;
}

.rso-filters-card {
  margin-bottom: 0;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.rso-filters-card__body {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.rso-filters-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 12px;
}

.rso-filters-main {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
}

.rso-filters-advanced {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e8edf3;
}

.rso-filters-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.rso-filter-field {
  min-width: 0;
  width: 100%;

  :deep(.custom-select__wrapper) {
    width: 100%;
  }

  &--date :deep(.custom-date) {
    max-width: none;
    min-width: 0;
    width: 100%;
  }

  &--date :deep(.custom-date__input),
  &--date :deep(.mx-input) {
    width: 100%;
  }

  :deep(.q-field) {
    width: 100%;
  }

  :deep(.q-field__control) {
    min-height: 40px;
  }

  :deep(.q-field__native) {
    min-height: 24px;
  }
}

.rso-filter-action-btn {
  font-size: 12px;
  min-height: 36px;
  width: 118px;
  min-width: 118px;
  padding: 0 8px;
}

@media (max-width: 1280px) {
  .rso-filters-toolbar {
    grid-template-columns: 1fr;
  }

  .rso-filters-main {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .rso-filters-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 720px) {
  .rso-filters-main,
  .rso-filters-advanced {
    grid-template-columns: 1fr;
  }

  .rso-filters-actions {
    width: 100%;
  }

  .rso-filter-action-btn {
    flex: 1 1 calc(50% - 4px);
    min-width: 0;
  }

}

.rso-progress {
  margin-bottom: 8px;
}

.rso-hint {
  padding: 32px 0;
  text-align: center;
  font-size: 13px;
  color: #64748b;
}

.rso-tree-card {
  border: 1px solid $gray-light;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  padding-bottom: 0;
}

.rso-tree-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
}

.rso-tree-toolbar__actions {
  display: flex;
  align-items: center;
}

.rso-tree-toolbar__total {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px 14px;
  margin-left: auto;
}

.rso-tree-toolbar__total-label {
  font-size: 12px;
  font-weight: 500;
  color: #475569;
}

.rso-tree-toolbar__total-item {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
}

.rso-tree-toolbar__total-suffix {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
}

.rso-tree-toolbar__total-value {
  font-size: 14px;
  font-weight: 700;
  color: #16a34a;
}

.rso-tree-toolbar__total-divider {
  display: inline-block;
  width: 1px;
  height: 18px;
  background: #cbd5e1;
  align-self: center;
  flex-shrink: 0;
}

.rso-tree-action-btn {
  font-size: 11px;
  color: #334155;
  border-radius: 6px;
}

.rso-tree-action-btn:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.rso-tree-scroll {
  overflow: auto;
  max-height: calc(100dvh - 120px);
  max-height: calc(100vh - 120px);
  -webkit-overflow-scrolling: touch;
  border-radius: 0 0 12px 12px;
  box-shadow: inset 0 1px 0 rgba(15, 23, 42, 0.06);
}

.rso-tree-grid {
  width: 100%;
  box-sizing: border-box;
}

.rso-grid-header-sticky {
  position: sticky;
  top: 0;
  z-index: 4;
  background: #eef4fb;
  box-shadow: 0 2px 6px rgba(30, 64, 175, 0.06);
}

.rso-tree-card :deep(.rso-smeta-grid-table) {
  table-layout: fixed;
  width: 100%;
  min-width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  color: #0f172a;
}

.rso-tree-card :deep(.rso-smeta-grid-table tbody td) {
  color: #0f172a;
}

.rso-tree-card :deep(.rso-smeta-grid-table tbody .amount) {
  color: inherit;
}

.rso-tree-card :deep(.rso-smeta-grid-table th),
.rso-tree-card :deep(.rso-smeta-grid-table td) {
  border: 1px solid #b8cfe4;
  padding: 3px 6px;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.rso-tree-card :deep(.rso-smeta-grid-table thead th) {
  background: #e9f1fb;
  font-weight: 600;
  color: #1e40af;
  text-align: center;
  padding: 8px 6px;
}

.rso-tree-card :deep(.rso-th--cost) {
  background: #dce8f5;
  color: #1e40af;
  border-left-color: #8aaed4;
}

.rso-tree-card :deep(.rso-th--wsum) {
  border-left: 2px solid #8aaed4;
}

.rso-tree-card :deep(.rso-th--ttl) {
  background: #c8dbf0;
  color: #0f172a;
}

.rso-tree-card :deep(.rso-th) {
  white-space: normal;
  line-height: 1.25;
  font-size: 11px;
  vertical-align: middle;
  hyphens: auto;
}

.rso-tree-card :deep(.rso-th--title) {
  font-weight: 700;
  font-size: 12px;
}

.rso-tree-card :deep(.rso-th--qty) {
  padding: 0;
  vertical-align: middle;
}

.rso-tree-card :deep(.rso-th__stack) {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  line-height: 1.25;
}

.rso-tree-card :deep(.rso-th__stack-line) {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-align: right;
  padding: 5px 6px;
}

.rso-tree-card :deep(.rso-th__stack-line--bottom) {
  border-top: 1px solid #8aaed4;
}

.rso-th--left {
  text-align: left;
}

.rso-th--right {
  text-align: right;
}

.rso-th--center {
  text-align: center;
}

.rso-tree-card :deep(.rso-node-row-td--total) {
  font-weight: 700;
}

.rso-tree-card :deep(.rso-node-row-td--total .amount) {
  font-weight: 700;
}
</style>
